import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { VendorPicker } from '@/features/quotes/components/VendorPicker'

import { Button } from '@/components/ui/Button'
import { Field } from '@/components/ui/Field'
import { FormSheet } from '@/components/ui/FormSheet'
import { useFormDraft } from '@/components/ui/useFormDraft'
import { Input } from '@/components/ui/Input'
import type { CostLine } from '@/types/domain'

/* The cost-line sheet: the phone editing primitive for the quote's tightest
 * surface, and the add path on both breakpoints.
 *
 * ONE price field, and it is the unit COST. A line carries a cost and a price
 * on the wire; this form collects the figure the source workbook has an
 * operator type — what the charge costs us, per unit — and `toCostLinePayload`
 * sends it as both sides. The customer's price is then carried up from it by
 * the markup panel, exactly as the workbook's profit-margin cell does.
 *
 * Two things follow, and both are why this comment exists:
 *
 *   - A line saved here reads **0% margin** until a markup is applied. That is
 *     the truth about a costing nobody has priced yet, and it is why this field
 *     is not the sell: the offer prints the sell, and a form collecting only
 *     that would leave the cost side empty and the margin reading 100%.
 *   - `sell` is absent from the draft entirely rather than kept as a hidden ''.
 *     An invisible required field that can fail validation is the trap, and a
 *     price derived by a button is not a thing to type twice.
 *
 * Editing seeds from `record.buy`, never `record.sell`: on a line that has been
 * through a markup those are different numbers, and offering the marked-up one
 * would write it back as the cost the NEXT markup is computed from — a quote
 * that gains a markup every time somebody fixes a typo.
 */

export interface CostLineDraft {
  description: string
  vendorName: string
  /** The directory row the vendor name resolves to, or null when it was typed
   *  (migration 0044). Derived by VendorPicker from the name, never entered. */
  vendorId: string | null
  quantity: string
  /** The unit COST. Named for what it is, so nothing downstream has to
   *  remember which side of the line this one figure belongs to. */
  unitPrice: string
}

function isValidAmount(raw: string): boolean {
  const normalised = raw.replace(/\s/g, '').replace(',', '.')
  return normalised !== '' && /^\d+(\.\d{1,2})?$/.test(normalised)
}

function isPositiveInteger(raw: string): boolean {
  return /^\d+$/.test(raw.trim()) && Number(raw) > 0
}

export function CostLineFormSheet({
  open,
  onClose,
  record,
  onSubmit,
  onDelete,
  pending,
  error,
}: {
  open: boolean
  onClose: () => void
  record?: CostLine
  onSubmit: (values: CostLineDraft) => void
  /** Removes an existing line. Absent when adding — there is nothing to remove
   *  yet, and a delete affordance on a blank form is a trap. */
  onDelete?: () => void
  pending?: boolean
  /** A refusal from the server, shown above the fields with the draft intact. */
  error?: string
}) {
  const { t } = useTranslation()

  /* A half-pressed delete must not survive the sheet closing, for the reason
     CargoFormSheet gives: reopening on another line with the confirmation
     already showing would put the dangerous button under a click meant for
     something else. Keyed on the record rather than reset in an effect. */
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const [confirmKey, setConfirmKey] = useState<string | null>(null)
  const currentKey = open ? (record?.id ?? 'new') : null
  if (confirmKey !== currentKey) {
    setConfirmKey(currentKey)
    setConfirmingDelete(false)
  }

  const [draft, setDraft, touched, markTouched] = useFormDraft<CostLineDraft>(
    open ? (record?.id ?? 'new') : null,
    () =>
      record
        ? {
            description: record.description,
            // The vendor is absent from a Sales user's DTO (FR-29, AD-6);
            // an absent value edits as empty, never as the marker text.
            vendorName:
              typeof record.vendorName === 'string' ? record.vendorName : '',
            // Suppressed and absent are one shape here on purpose — see the
            // server's CostLineDTO.VendorID. Either way there is no
            // counterparty to reopen on, and the field is locked below anyway.
            vendorId:
              typeof record.vendorId === 'string' ? record.vendorId || null : null,
            quantity: String(record.quantity),
            unitPrice: record.buy.amount,
          }
        : emptyLine,
  )

  const vendorHidden =
    record?.vendorName !== undefined && typeof record.vendorName !== 'string'

  const descriptionError =
    touched && !draft.description.trim() ? t('common.form.required') : undefined
  const quantityError =
    touched && !isPositiveInteger(draft.quantity)
      ? t('common.form.invalidNumber')
      : undefined
  const unitPriceError =
    touched && !isValidAmount(draft.unitPrice)
      ? t('common.form.invalidNumber')
      : undefined

  const invalid =
    !draft.description.trim() ||
    !isPositiveInteger(draft.quantity) ||
    !isValidAmount(draft.unitPrice)

  return (
    <FormSheet
      open={open}
      onClose={onClose}
      title={record ? t('quotes.costLines.edit') : t('quotes.costLines.add')}
      pending={pending}
      // A server refusal outranks the local one: it is the newer and more
      // specific answer, and showing "fix the fields" over it would send the
      // user looking at inputs that are already correct.
      error={error ?? (touched && invalid ? t('common.form.fixFields') : undefined)}
      onSubmit={() => {
        markTouched()
        if (invalid) return
        onSubmit(draft)
      }}
    >
      <Field
        label={t('quotes.costLines.description')}
        required
        error={descriptionError}
      >
        {(props) => (
          <Input
            value={draft.description}
            onChange={(e) => setDraft({ ...draft, description: e.target.value })}
            {...props}
          />
        )}
      </Field>

      {/* The counterparty directory, as suggestions rather than as a gate: a
          name that matches a row links to it, a name that matches nothing is
          kept as typed. VendorPicker's own header carries the two reasons —
          migration 0019's, and FR-29's. */}
      <Field
        label={t('quotes.costLines.vendor')}
        hint={
          vendorHidden
            ? t('common.suppressed.reason')
            : t('quotes.costLines.vendorHint')
        }
      >
        {(props) => (
          <VendorPicker
            value={draft.vendorName}
            disabled={vendorHidden}
            onChange={(vendorName, vendorId) =>
              setDraft({ ...draft, vendorName, vendorId })
            }
            placeholder={t('quotes.costLines.vendorPlaceholder')}
            {...props}
          />
        )}
      </Field>

      <Field label={t('quotes.costLines.quantity')} required error={quantityError}>
        {(props) => (
          // Numeric keyboards on money and quantity fields.
          <Input
            inputMode="numeric"
            value={draft.quantity}
            onChange={(e) => setDraft({ ...draft, quantity: e.target.value })}
            className="asa-num"
            {...props}
          />
        )}
      </Field>

      <Field label={t('quotes.costLines.unitPrice')} required error={unitPriceError}>
        {(props) => (
          <Input
            inputMode="decimal"
            value={draft.unitPrice}
            onChange={(e) => setDraft({ ...draft, unitPrice: e.target.value })}
            className="asa-num"
            {...props}
          />
        )}
      </Field>

      {/* Removing sits at the FOOT of the form, apart from the footer's Save,
          for the reason the cargo sheet gives: the dangerous action stays off
          the same muscle memory as the safe one. Two presses, because a removed
          line takes its share of the margin with it. */}
      {onDelete ? (
        <div className="border-line-hair mt-1 border-t pt-3">
          {confirmingDelete ? (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-text-body flex-1 text-[12.5px] leading-snug">
                {t('quotes.costLines.deleteConfirm')}
              </span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setConfirmingDelete(false)}
                disabled={pending}
              >
                {t('common.actions.cancel')}
              </Button>
              <Button
                variant="danger"
                size="sm"
                onClick={onDelete}
                disabled={pending}
              >
                {t('quotes.costLines.deleteConfirmAction')}
              </Button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmingDelete(true)}
              disabled={pending}
              className="text-negative cursor-pointer text-[12.5px] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t('quotes.costLines.delete')}
            </button>
          )}
        </div>
      ) : null}
    </FormSheet>
  )
}

const emptyLine: CostLineDraft = {
  description: '',
  vendorName: '',
  vendorId: null,
  quantity: '1',
  unitPrice: '',
}
